import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Privacy Policy"
        description="Privacy Policy for T&M Construction. Learn how we collect, use, and protect your personal information."
        canonical="/privacy"
      />
      
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: January 2025</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              T&M Builders Suffolk LTD (trading as "T&M Construction") is committed to protecting your 
              privacy and personal data. This Privacy Policy explains how we collect, use, store, and 
              protect your information when you use our website or services.
            </p>
            <p className="text-gray-700 mb-4">
              We are registered in England and Wales (Company Number: 14672875) and comply with the 
              UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            <p className="text-gray-700 mb-4">
              We may collect the following types of personal information:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Contact Information:</strong> Name, email address, phone number, postal address</li>
              <li><strong>Project Details:</strong> Information about your property and required services</li>
              <li><strong>Communication Records:</strong> Emails, messages, and phone call records</li>
              <li><strong>Reviews and Testimonials:</strong> Feedback you provide about our services</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on site, referral source</li>
              <li><strong>IP Address:</strong> For security and analytics purposes</li>
              <li><strong>Cookies:</strong> As described in our Cookie section below</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">
              We use your personal information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Service Delivery:</strong> To provide quotes, schedule work, and deliver our services</li>
              <li><strong>Communication:</strong> To respond to enquiries, send project updates, and provide customer support</li>
              <li><strong>Marketing:</strong> To send promotional materials (only with your consent)</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
              <li><strong>Website Improvement:</strong> To analyse usage and improve our website</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Legal Basis for Processing</h2>
            <p className="text-gray-700 mb-4">
              We process your personal data based on the following legal grounds:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Contract:</strong> Processing necessary for providing our services</li>
              <li><strong>Legitimate Interests:</strong> For business operations and improving our services</li>
              <li><strong>Consent:</strong> For marketing communications and optional data collection</li>
              <li><strong>Legal Obligation:</strong> For compliance with applicable laws</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Data Sharing</h2>
            <p className="text-gray-700 mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Service Providers:</strong> Trusted third parties who assist our operations (e.g., email services, payment processors)</li>
              <li><strong>Subcontractors:</strong> When necessary to complete your project</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our legal rights</li>
            </ul>
            <p className="text-gray-700 mb-4">
              We do not sell your personal information to third parties.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organisational measures to protect your personal 
              data against unauthorised access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Secure SSL encryption on our website</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
              <li>Staff training on data protection</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal data only for as long as necessary to fulfil the purposes for 
              which it was collected, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Customer records: 7 years after last interaction (for legal and accounting purposes)</li>
              <li>Project records: 7 years after project completion</li>
              <li>Marketing data: Until you withdraw consent</li>
              <li>Website analytics: 26 months</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Your Rights</h2>
            <p className="text-gray-700 mb-4">
              Under UK GDPR, you have the following rights:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of data processing</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise any of these rights, please contact us at info@tandmbuilders.co.uk.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Cookies</h2>
            <p className="text-gray-700 mb-4">
              Our website uses cookies to enhance your browsing experience. Cookies are small text 
              files stored on your device. We use:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Essential Cookies:</strong> Necessary for website functionality</li>
              <li><strong>Analytics Cookies:</strong> To understand how visitors use our site</li>
              <li><strong>Marketing Cookies:</strong> To deliver relevant advertisements (with consent)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings. Disabling certain cookies may 
              affect website functionality.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Third-Party Links</h2>
            <p className="text-gray-700 mb-4">
              Our website may contain links to third-party websites (e.g., social media platforms). 
              We are not responsible for the privacy practices of these external sites. We encourage 
              you to read their privacy policies.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our services are not directed at individuals under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected information 
              from a child, please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. Any changes will be posted on 
              this page with an updated revision date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your rights, 
              please contact us:
            </p>
            <ul className="list-none text-gray-700 mb-4">
              <li><strong>Data Controller:</strong> T&M Builders Suffolk LTD</li>
              <li><strong>Email:</strong> info@tandmbuilders.co.uk</li>
              <li><strong>Phone:</strong> +44 0333 335 5532</li>
              <li><strong>Address:</strong> Ipswich, UK</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Complaints</h2>
            <p className="text-gray-700 mb-4">
              If you are not satisfied with how we handle your personal data, you have the right to 
              lodge a complaint with the Information Commissioner's Office (ICO):
            </p>
            <ul className="list-none text-gray-700 mb-4">
              <li><strong>Website:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">ico.org.uk</a></li>
              <li><strong>Phone:</strong> 0303 123 1113</li>
            </ul>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <p className="text-gray-600 text-sm">
                This Privacy Policy is governed by the laws of England and Wales and complies with 
                the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
            </div>

            <div className="mt-8 flex space-x-4">
              <Link to="/terms" className="text-orange-600 hover:text-orange-700 font-medium">
                Terms and Conditions →
              </Link>
              <Link to="/contact" className="text-orange-600 hover:text-orange-700 font-medium">
                Contact Us →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
